// ─── Extended-hours trading in the app (sprint 19, stage 1) ─────────────────
//
// The quotes come from /.netlify/functions/extended-hours (Yahoo Finance,
// netlify/functions/lib/extendedHours.js says how each field is read). This
// file decides what of them is shown:
//
//   · a holding's pre-market or after-hours move · only while Yahoo says the
//     market is in that window (marketState PRE / POST), and only when the
//     last extended trade is from the last FRESH_MIN minutes. Anything else:
//     null, and nothing is drawn
//   · the futures · the same freshness rule
//   · the portfolio's ESTIMATED move · Σ (weight × extended move), the weight
//     by value at the last regular price. Every input is a price Yahoo
//     reported: a holding's last trade in the window, whatever its age, or,
//     when it has not traded in the window at all, its close (it has not
//     moved since). A holding Yahoo did not answer for is left out, never
//     filled in, and the estimate says how many holdings it is based on. It
//     is shown only when its newest trade is from the last FRESH_MIN
//     minutes, and always labelled "משוער".
import { useEffect, useState } from "react";

export const FRESH_MIN = 30;
const TTL_MS = 60 * 1000;
// A little under the refresh interval: the memo is stamped when the answer
// arrives, the interval counts from the request, and an equal TTL served
// every second tick from the memo (stage 1 review).
const MEMO_MS = TTL_MS - 5000;
let memo = null;   // { key, at, data }

export async function fetchExtended(symbols) {
  const list = [...new Set((symbols || []).filter(Boolean).map(s => String(s).toUpperCase()))].sort();
  const key = list.join(",");
  if (memo && memo.key === key && Date.now() - memo.at < MEMO_MS) return memo.data;
  try {
    const res = await fetch(`/.netlify/functions/extended-hours?symbols=${encodeURIComponent(key)}`);
    if (!res.ok) return null;
    const data = await res.json();
    memo = { key, at: Date.now(), data };
    return data;
  } catch {
    return null;
  }
}

const fresh = (iso, now) => {
  const t = new Date(iso).getTime();
  return Number.isFinite(t) && t <= now.getTime() + 60000 && now.getTime() - t <= FRESH_MIN * 60000;
};

// One holding's extended move, or null.
// → { state: "PRE" | "POST", price, pct, time }
export function extendedFor(q, now = new Date()) {
  if (!q || q.kind !== "equity") return null;
  if (q.marketState === "PRE" && Number.isFinite(q.preMarketChangePercent) && fresh(q.preMarketTime, now)) {
    return { state: "PRE", price: q.preMarketPrice, pct: q.preMarketChangePercent, time: q.preMarketTime };
  }
  if (q.marketState === "POST" && Number.isFinite(q.postMarketChangePercent) && fresh(q.postMarketTime, now)) {
    return { state: "POST", price: q.postMarketPrice, pct: q.postMarketChangePercent, time: q.postMarketTime };
  }
  return null;
}

// A future's move against the previous settlement, or null when not fresh.
export function futureFor(q, now = new Date()) {
  if (!q || q.kind !== "future" || !Number.isFinite(q.changePercent) || !fresh(q.time, now)) return null;
  return { price: q.price, pct: q.changePercent, time: q.time };
}

// A holding's last trade in the current extended window, whatever its age,
// or its close when it has not traded in the window · null when Yahoo gave
// no usable answer, or the market is not in an extended window.
function lastExtended(q) {
  if (!q || q.kind !== "equity" || !(q.regularMarketPrice > 0)) return null;
  const pre = q.marketState === "PRE", post = q.marketState === "POST";
  if (!pre && !post) return null;
  const pct = pre ? q.preMarketChangePercent : q.postMarketChangePercent;
  const time = pre ? q.preMarketTime : q.postMarketTime;
  return { state: q.marketState, pct: Number.isFinite(pct) ? pct : 0, time: Number.isFinite(pct) ? time : null };
}

/**
 * The portfolio's estimated extended-hours move.
 * → { state, pct, covered, total, time } or null
 *   covered · holdings in the estimate · traded · of them, those with an
 *   extended trade in the window · total · holdings with a price
 *   time · the newest extended trade among them, within FRESH_MIN minutes
 */
export function estimateExtended(portfolio, quotes, now = new Date()) {
  let total = 0;
  const rows = [];
  for (const p of portfolio || []) {
    if (p.quote === "none" || !(p.shares > 0)) continue;
    total += 1;
    const q = quotes?.[String(p.symbol).toUpperCase()];
    const move = lastExtended(q);
    if (move) rows.push({ symbol: String(p.symbol).toUpperCase(), value: q.regularMarketPrice * p.shares, move });
  }
  const states = [...new Set(rows.map(r => r.move.state))];
  if (states.length !== 1) return null;
  const value = rows.reduce((a, r) => a + r.value, 0);
  const time = rows.map(r => r.move.time).filter(Boolean).sort().pop() || null;
  if (!(value > 0) || !time || !fresh(time, now)) return null;
  const pct = rows.reduce((a, r) => a + (r.value / value) * r.move.pct, 0);
  // What each holding adds, in percentage points (sprint 21, 4c): the sum
  // can then be checked on the screen. Lots of one symbol are one holding.
  const bySym = new Map();
  for (const r of rows) {
    const x = bySym.get(r.symbol) || { symbol: r.symbol, value: 0, pct: r.move.pct };
    x.value += r.value;
    bySym.set(r.symbol, x);
  }
  const contributions = [...bySym.values()]
    .map(x => ({ symbol: x.symbol, weight: (x.value / value) * 100, pct: x.pct, points: (x.value / value) * x.pct }))
    .sort((a, b) => Math.abs(b.points) - Math.abs(a.points));
  return { state: states[0], pct, covered: rows.length, traded: rows.filter(r => r.move.time).length, total, time, contributions };
}

// While New York is in the pre-market or after the close (marketStatus.js),
// the extended quotes for the holdings and the two futures, every minute.
// null outside those windows and until the first answer.
export function useExtendedHours(symbols, marketState) {
  const [data, setData] = useState(null);
  // Re-rendered every interval even when the fetch fails, so a figure past
  // its FRESH_MIN window is dropped by the next render (stage 1 review).
  const [, setTick] = useState(0);
  const active = marketState === "pre" || marketState === "post";
  const key = [...new Set((symbols || []).filter(Boolean))].sort().join(",");
  useEffect(() => {
    if (!active) { setData(null); return undefined; }
    let alive = true;
    const load = () => fetchExtended(key ? key.split(",") : []).then(d => { if (alive && d) setData(d); });
    load();
    const id = setInterval(() => { setTick(t => t + 1); load(); }, TTL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [active, key]);
  return active ? data : null;
}

export const EXTENDED_INFO = "מסחר מורחב · מחזור מסחר נמוך, המחירים עשויים להשתנות בפתיחה";
